import React, { useEffect, useRef, useState } from "react";
import { CloudUpload, Loader } from "lucide-react";
import { getPractice, notify } from "../../helpers";
import { Drawer } from 'vaul';
import Spacing from "../Spacing";
import { importClaims } from "../../services/npiManagement";
import Select from "react-select";

export default function ClaimCheckImportModal(props) {
  const { 
    account_id,
    location_id,
    activeModal= false,
    setActiveModal = () => {},
    activeLoader= false,
    setActiveLoader = () => {},
    previousScrollPosition = 0,
  } = props;

  const defaultValues = {
    account_id: account_id,
    location_id: location_id,
    file: null,
    recheck: false,
    emr_type: "",
  }

  const [formData, setFormData] = useState(defaultValues);
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState(false);
  const [uploadDisabled, setUploadDisabled] = useState(true);
  const [forceRecheck, setForceRecheck] = useState(false);
  const dropdownRef = useRef(null);
  const [ehrType] = useState([
    { value: 'ECW', label: 'ECW', },
    { value: 'MDLand', label: 'MDLand', },
    { value: 'VeradigmEHR', label: 'VeradigmEHR', },
    { value: 'AthenaHealth', label: 'AthenaHealth', }
  ]);

  const validEhrValues = new Set(ehrType.map(o => o.value));

  useEffect(() => {
    const preferred = getPractice("emr_type");
    const nextEmr = validEhrValues.has(preferred) ? preferred : "MDLand";

    setFormData(prev => ({
      ...prev,
      location_id,
      account_id,
      emr_type: nextEmr,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location_id, account_id]);

  const validTypes = new Set([
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.ms-excel",
    "text/csv",
    "application/csv",
  ]);

  const filterValidFiles = (fileList) => {
    const arr = Array.from(fileList || []);
    const invalid = arr.some((f) => !validTypes.has(f.type));
    return { files: invalid ? [] : arr, invalid };
  };
  const handleFileChange = (e) => {
    const { files, invalid } = filterValidFiles(e.target.files);
    setFileError(invalid || files.length === 0);
    setUploadDisabled(files.length === 0);
    setFile(files[0]);
    setFormData({
      ...formData,
      file: files[0]
    });
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    e.currentTarget.classList.remove("dragover");
    const { files, invalid } = filterValidFiles(e.dataTransfer.files);
    setFileError(invalid || files.length === 0);
    setUploadDisabled(files.length === 0);
    setFile(files[0]);
    setFormData({
      ...formData,
      file: files[0]
    });
  };

  const handleImportClaim = async (e) => {
    e.preventDefault();
    e.target.disabled = true;
    setActiveLoader(true);
    setUploadDisabled(true);

    try {
      const postData = {
        ...formData,
        recheck: forceRecheck
      };
      const response = await importClaims(postData);

      if (!response) return;
      const contentType = response.headers?.["content-type"] || "";
      const isBlob = response.data instanceof Blob;

      if (isBlob && /application\/json|application\/problem\+json|text\/plain/i.test(contentType)) {
        const text = await response.data.text();
        try {
          const json = JSON.parse(text);
          const msg = json?.message || json?.detail || text;
          notify("error", msg, 5);
        } catch {
          notify("error", text.slice(0, 500), 5);
        }
        return;
      }

      if (!isBlob && typeof response.data === "object" && response.data !== null) {
        if (response.data.status === "error") {
          notify("error", response.data.message || "Unknown error", 5);
          return;
        }
      }

      const dispo = response.headers?.["content-disposition"] || "";
      const m = /filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i.exec(dispo);
      const serverName = m ? decodeURIComponent(m[1]) : null;

      // Fallback file name
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, "0");
      const day = String(now.getDate()).padStart(2, "0");
      const hours = String(now.getHours()).padStart(2, "0");
      const minutes = String(now.getMinutes()).padStart(2, "0");
      const seconds = String(now.getSeconds()).padStart(2, "0");
      const fallback = `Checed_claims_${year}_${month}_${day}_${hours}_${minutes}_${seconds}.xlsx`;
      const filename = serverName || fallback;

      // Ensure XLSX MIME
      const xlsxBlob = isBlob
        ? response.data
        : new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });

      // Download
      if (window.navigator && window.navigator.msSaveOrOpenBlob) {
        window.navigator.msSaveOrOpenBlob(xlsxBlob, filename);
      } else {
        const url = window.URL.createObjectURL(xlsxBlob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      if (error?.response && error?.response?.data && error?.response?.data?.detail) {
        notify("error", error?.response?.data?.detail, 5);
      }
    } finally {
      setActiveLoader(false);
      setUploadDisabled(false);
    }
    e.target.disabled = false;   
  };

  const handleForceRecheck = (e) => {
    const isChecked = e.target.checked;
    if(isChecked){
      setForceRecheck(true);
    } else {
      setForceRecheck(false)
    }
  };

  const handleDrawerClose = () => {
    setActiveModal(false);
    window.scrollTo(0, previousScrollPosition);
  };

  return (
    <div style={{ height: "100%" }} ref={dropdownRef}>
      <Drawer.Root open={activeModal} onOpenChange={handleDrawerClose} dismissible={false}>
        <Drawer.Trigger className="docpro_single_patiant_modal_btn">
        </Drawer.Trigger>
        <Drawer.Portal>
          <Drawer.Overlay className="docpro_drawer_overlay"/>
          <Drawer.Content className="docpro_drawer_content docpro_drawer_style_4 large_medium" onPointerDownOutside={(e) => e.preventDefault()} onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
            <Drawer.Title className="sr-only">Import claim</Drawer.Title>
            <Drawer.Description className="sr-only">Import all claim</Drawer.Description>
            <div className="docpro_modal_wrap docpro_modal_lg no_padding">
              <div className="docpro_modal small import">

                <form onSubmit={handleImportClaim} className="modal_height_100">
                  <div>
                    <h2 className="docpro_fs_24 docpro_mb_0 docpro_small_modal_padding_1">Import Claim</h2>
                    <div className="docpro_badge_green" style={{ padding: '12px 24px' }}>
                      <p className="mb-0"><span style={{ fontWeight: 600 }}>Account Name:</span> {getPractice("account_name")}</p>
                      <p className="mb-0"><span style={{ fontWeight: 600 }}>Location Name:</span> {getPractice("location_name")}</p>
                    </div>
                    <Spacing lg="20" sm="20" />

                    <div className="docpro_small_modal_padding_2">
                      <label className="docpro_fw_500 docpro_primary_color"> EMR Type </label>
                      <Select
                        name="emr_type"
                        options={ehrType}
                        onChange={(selectedOption) => {
                          const value = selectedOption?.value;
                          if (value && validEhrValues.has(value)) {
                            setFormData(prev => ({ ...prev, emr_type: value }));
                          }
                        }}
                        placeholder="Search..."
                        isSearchable={true}
                        value={ehrType.find(
                          (option) => option.value === formData.emr_type
                        )}
                        className="react-select-container w-full"
                        classNamePrefix="react-select"
                        styles={{
                          control: (base) => ({
                            ...base,
                            minHeight: '40px',
                            borderRadius: '8px',
                            borderColor: 'var(--border)',
                          }),
                        }}
                      />
                      <Spacing lg="20" sm="20" />

                      <label className="docpro_primary_color">
                        <span className="docpro_fw_500">Claim File</span> <small className="docpro_red_color">(.xlsx, .xls, .csv)</small>
                      </label>
                      <div
                        className="docpro_input_file_container"
                        onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('dragover'); }}
                        onDragLeave={(e) => e.currentTarget.classList.remove('dragover')}
                        onDrop={handleFileDrop}
                        role="button"
                        tabIndex={0}
                        aria-label="Upload Excel/CSV files by dragging or clicking"
                        style={{ minHeight: '85px' }}
                      >
                        <span className="docpro_file_icon" aria-hidden="true">
                          <CloudUpload height={28} width={28} />
                        </span>
                        <div className="docpro_file_text">
                          <div className="docpro_file_title">
                            {file ? file.name : "Drag and drop claim file"}
                          </div>
                          <div className="docpro_file_hint">
                            {file ? '' : 'here, or click to upload'}
                          </div>
                        </div>
        
                        <input
                          type="file"
                          accept=".xlsx,.xls,.csv"
                          className="docpro_file_hidden_input"
                          onChange={handleFileChange}
                        />
                      </div>
                      {fileError && (
                        <p className="docpro_error_message mt-1 docpro_mb_0"> Please upload a valid Excel/CSV file. </p>
                      )}
                      <Spacing lg="20" sm="20" />
                      <div className="docpro_checkbox" >
                        <input
                          type="checkbox"
                          value={true}
                          name="recheck" 
                          onChange={handleForceRecheck}
                        />
                        <label style={{ marginLeft: "8px" }}> Skip cached history - perform a new check</label>
                      </div>
                    </div>
                  </div>

                  <div className="docpro_modal_btns small_buttons docpro_mt_auto docpro_small_modal_padding_3">
                    <button
                      type="button"
                      className="docpro_bordered_btn docpro_fw_600 size_lg"
                      onClick={() => handleDrawerClose()}
                    >
                      Cancel
                    </button>
                    <button
                      type='submit'
                      className="docpro_bordered_btn docpro_fw_600 docpro_filed_btn size_lg"
                      disabled={uploadDisabled ? true : false}
                    >
                      Upload
                    </button>
                  </div>
                </form>

                {activeLoader &&
                  <div className="flex items-center loading" style={{ width: '100%', height: '100%', position: 'absolute', top: 0, background: '#ffffff9c', borderRadius: '10px', justifyContent: 'center' }}>
                    <span className="docpro_loading" style={{ position: 'inherit' }}>
                      <Loader width={46} height={46} style={{ color: 'var(--accent)' }} />
                    </span>
                  </div>
                }
              </div>
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>
    </div>
  );
}
